import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "attribution_session_id";
const FIRST_TOUCH_KEY = "attribution_first_touch";
const LAST_TOUCH_KEY = "attribution_last_touch";
const SENT_KEY = "attribution_sent_for_session";

// Pages that are payment-processor return URLs or internal redirects.
// Visits to these must never overwrite landing pages or count as a new touch.
const EXCLUDED_LANDING_PATHS = [
  "/betaling-succes",
  "/betaling-success",
  "/betaling-geannuleerd",
  "/betaling-cancelled",
  "/payment-success",
  "/payment-canceled",
  "/payment-cancelled",
  "/checkout/success",
  "/checkout/cancel",
  "/auth/callback",
];

const isExcludedPath = (pathname: string): boolean => {
  const p = (pathname || "").toLowerCase();
  return EXCLUDED_LANDING_PATHS.some((ex) => p === ex || p.startsWith(ex + "/"));
};

export type AttributionTouch = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
  fbclid: string | null;
  referrer: string | null;
  landing_path: string | null;
  captured_at: string;
};

export const getAttributionSessionId = (): string => {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
};

const parseUrl = (): AttributionTouch => {
  const params = new URLSearchParams(window.location.search);
  const ref = document.referrer || "";
  const ignoreRef = (() => {
    try {
      if (!ref) return true;
      const host = new URL(ref).host.toLowerCase().replace(/^www\./, "");
      if (host === window.location.host.toLowerCase().replace(/^www\./, "")) return true;
      // Payment processors / checkout redirects should not overwrite real attribution
      const PAYMENT_HOSTS = [
        "stripe.com", "checkout.stripe.com",
        "mollie.com", "paypal.com", "paypal.me",
        "adyen.com", "checkout.com", "klarna.com",
      ];
      return PAYMENT_HOSTS.some((h) => host === h || host.endsWith("." + h));
    } catch {
      return true;
    }
  })();
  // Google Ads auto-tagging can use gclid OR gbraid/wbraid (iOS/web app context).
  // gad_source / gad_campaignid are auto-tagging signals too. Treat any of these
  // as a paid Google click so attribution doesn't show "direct".
  const gclid = params.get("gclid");
  const gbraid = params.get("gbraid");
  const wbraid = params.get("wbraid");
  const gadSource = params.get("gad_source");
  const gadCampaign = params.get("gad_campaignid");
  const isGoogleAds = !!(gclid || gbraid || wbraid || gadSource || gadCampaign);

  const utmSource = params.get("utm_source") || (isGoogleAds ? "google" : null);
  const utmMedium = params.get("utm_medium") || (isGoogleAds ? "cpc" : null);
  const utmCampaign = params.get("utm_campaign") || (gadCampaign ? `gads_${gadCampaign}` : null);

  return {
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_term: params.get("utm_term"),
    utm_content: params.get("utm_content"),
    gclid: gclid || gbraid || wbraid || null,
    fbclid: params.get("fbclid"),
    referrer: ignoreRef ? null : ref || null,
    landing_path: window.location.pathname + window.location.search,
    captured_at: new Date().toISOString(),
  };
};

const hasSignal = (t: AttributionTouch): boolean =>
  !!(t.utm_source || t.utm_medium || t.utm_campaign || t.gclid || t.fbclid || t.referrer);

export const getFirstTouch = (): AttributionTouch | null => {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const getLastTouch = (): AttributionTouch | null => {
  try {
    const raw = localStorage.getItem(LAST_TOUCH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Capture and persist attribution. Runs once per browser session unless new
 * UTM/gclid/fbclid parameters appear (then a new touch is recorded).
 */
export const captureAttribution = async (): Promise<void> => {
  try {
    // Skip payment-return and auth-callback URLs entirely
    if (isExcludedPath(window.location.pathname)) return;

    const touch = parseUrl();
    const sessionId = getAttributionSessionId();
    const sentMarker = sessionStorage.getItem(SENT_KEY);
    const newSignal = hasSignal(touch);

    // Skip if already sent in this tab session and no fresh marketing signal
    if (sentMarker && !newSignal) return;

    // Always set first touch if absent
    if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
      localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
    }
    // Update last touch only on signal (or first ever visit)
    if (newSignal || !localStorage.getItem(LAST_TOUCH_KEY)) {
      localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(touch));
    }

    sessionStorage.setItem(SENT_KEY, "1");

    const { data: userData } = await supabase.auth.getUser();

    await supabase.from("attribution_touches").insert({
      session_id: sessionId,
      user_id: userData?.user?.id ?? null,
      utm_source: touch.utm_source,
      utm_medium: touch.utm_medium,
      utm_campaign: touch.utm_campaign,
      utm_term: touch.utm_term,
      utm_content: touch.utm_content,
      gclid: touch.gclid,
      fbclid: touch.fbclid,
      referrer: touch.referrer,
      landing_path: touch.landing_path,
      user_agent: navigator.userAgent.slice(0, 500),
    });
  } catch {
    // Silent: never block UX for analytics
  }
};

/**
 * Link any anonymous attribution touches from this browser session to a
 * signed-in user, and stamp first/last touch attribution onto their profile.
 * Safe to call multiple times.
 */
export const linkAttributionToUser = async (userId: string): Promise<void> => {
  if (!userId) return;
  try {
    const sessionId = getAttributionSessionId();
    await supabase.rpc("link_attribution_to_user", {
      p_session_id: sessionId,
      p_user_id: userId,
    });
  } catch {
    // Silent
  }
};